import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';

import { AUDIO_SPEEDS, useAudioControls, type AudioSpeed } from './AudioControlsContext';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';

const WAVE_BAR_COUNT = 4;
const WAVE_WIDTH_PX = 14;
const WAVE_HEIGHT_PX = 16;
const WAVE_BAR_WIDTH_PX = 2;
const WAVE_BAR_RADIUS_PX = 1;
const WAVE_ANIM_DURATION_MS = 1400;
const WAVE_BAR_DELAYS_MS = [0, -350, -700, -200] as const;
const WAVE_KEYFRAMES: Keyframe[] = [
  { transform: 'scaleY(0.15)' },
  { transform: 'scaleY(0.55)' },
  { transform: 'scaleY(0.15)' },
  { transform: 'scaleY(1)' },
  { transform: 'scaleY(0.15)' },
];

const PLAY_PAUSE_SIZE_PX = 28;
const SPEED_PILL_WIDTH_PX = 50;
const SPEED_PILL_HEIGHT_PX = 28;
// NA-357: deliberate one-off between spacing.md (12px) and spacing.lg (16px).
const CLUSTER_GAP_PX = 14;

const HOVER_FILL = '#F3F3F3';

const POPOVER_SHADOW = '0 4px 12px rgba(0,0,0,0.10)';
const POPOVER_RADIUS_PX = 8;
const POPOVER_VERTICAL_GAP_PX = 6;

function formatSpeedLabel(speed: AudioSpeed): string {
  // Trimmed format: "1x", "0.5x", "1.25x" — drop trailing zeros, keep leading zero.
  return `${speed}x`;
}

interface WaveformProps {
  isPlaying: boolean;
  color: string;
}

function Waveform({ isPlaying, color }: WaveformProps): React.ReactElement {
  const barRefs = useRef<(HTMLDivElement | null)[]>([]);
  const animationsRef = useRef<Animation[]>([]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof Element === 'undefined' || typeof Element.prototype.animate !== 'function') return;

    const bars = barRefs.current.slice(0, WAVE_BAR_COUNT);
    const animations = bars.map((bar, i) => {
      if (!bar) return null;
      return bar.animate(WAVE_KEYFRAMES, {
        duration: WAVE_ANIM_DURATION_MS,
        delay: WAVE_BAR_DELAYS_MS[i] ?? 0,
        iterations: Infinity,
        easing: 'cubic-bezier(0.4, 0, 0.6, 1)',
      });
    }).filter((a): a is Animation => a !== null);
    animationsRef.current = animations;

    return () => {
      animations.forEach((a) => a.cancel());
      animationsRef.current = [];
    };
  }, []);

  useEffect(() => {
    animationsRef.current.forEach((a) => {
      if (isPlaying) {
        a.play();
      } else {
        a.pause();
      }
    });
  }, [isPlaying]);

  const containerStyle: CSSProperties = {
    width: `${WAVE_WIDTH_PX}px`,
    height: `${WAVE_HEIGHT_PX}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  };

  const barStyle: CSSProperties = {
    width: `${WAVE_BAR_WIDTH_PX}px`,
    height: '100%',
    backgroundColor: color,
    borderRadius: `${WAVE_BAR_RADIUS_PX}px`,
    transformOrigin: 'center center',
    transform: 'scaleY(0.15)',
  };

  return (
    <div style={containerStyle} aria-hidden="true">
      {Array.from({ length: WAVE_BAR_COUNT }).map((_, i) => (
        <div
          key={i}
          ref={(el) => {
            barRefs.current[i] = el;
          }}
          style={barStyle}
        />
      ))}
    </div>
  );
}

interface PlayPauseIconProps {
  isPlaying: boolean;
  color: string;
}

function PlayPauseIcon({ isPlaying, color }: PlayPauseIconProps): React.ReactElement {
  if (isPlaying) {
    // Pause: two filled vertical bars
    return (
      <svg width="10" height="12" viewBox="0 0 14 16" fill={color} aria-hidden="true">
        <path d="M1.75 0C0.784 0 0 0.768 0 1.714v12.572C0 15.232 0.784 16 1.75 16h2.333c0.967 0 1.75-0.768 1.75-1.714V1.714C5.833 0.768 5.05 0 4.083 0H1.75ZM9.917 0C8.95 0 8.167 0.768 8.167 1.714v12.572C8.167 15.232 8.95 16 9.917 16h2.333c0.967 0 1.75-0.768 1.75-1.714V1.714C14 0.768 13.216 0 12.25 0H9.917Z" />
      </svg>
    );
  }
  // Play: filled right-pointing triangle. The triangle's visual mass sits to
  // the left of its bounding box center; nudge 1px right so it looks centered.
  return (
    <svg
      width="10"
      height="12"
      viewBox="0 0 13 14"
      fill={color}
      aria-hidden="true"
      style={{ transform: 'translateX(1px)' }}
    >
      <path d="M1.85 0.153c-0.388-0.213-0.856-0.203-1.238 0.022C0.231 0.4 0 0.81 0 1.25v11.5c0 0.44 0.234 0.85 0.613 1.075 0.378 0.225 0.85 0.234 1.237 0.022l10.5-5.75C12.75 7.878 13 7.456 13 7s-0.25-0.878-0.65-1.097L1.85 0.153Z" />
    </svg>
  );
}

interface SpeedPopoverProps {
  selectedSpeed: AudioSpeed;
  onSelect: (speed: AudioSpeed) => void;
  capsuleVerticalPadding: string;
}

function SpeedPopover({
  selectedSpeed,
  onSelect,
  capsuleVerticalPadding,
}: SpeedPopoverProps): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const [hoveredSpeed, setHoveredSpeed] = useState<AudioSpeed | null>(null);
  // NA-242: audio controls use the fixed UI accent — never the account accent.
  const accentColor = tokens.ui.fixedAccent;

  const popoverStyle: CSSProperties = {
    position: 'absolute',
    top: `calc(100% + ${POPOVER_VERTICAL_GAP_PX}px)`,
    left: '50%',
    // The wrapper is the 28px speed button rather than the padded capsule.
    // Translate past the active capsule padding so the 6px gap remains outside it.
    transform: `translateX(-50%) translateY(${capsuleVerticalPadding})`,
    backgroundColor: tokens.backgrounds.page,
    border: `1px solid ${tokens.borders.default}`,
    borderRadius: `${POPOVER_RADIUS_PX}px`,
    boxShadow: POPOVER_SHADOW,
    padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
    display: 'flex',
    flexDirection: 'column',
    minWidth: '80px',
    boxSizing: 'border-box',
    zIndex: 5,
  };

  return (
    <div style={popoverStyle} role="listbox" aria-label={t('audio_controls.narration_speed')}>
      {AUDIO_SPEEDS.map((speed) => {
        const isActive = speed === selectedSpeed;
        const isHovered = hoveredSpeed === speed && !isActive;
        const itemStyle: CSSProperties = {
          background: 'none',
          border: 'none',
          padding: `${tokens.spacing.xs} 0`,
          textAlign: 'left',
          fontFamily: 'inherit',
          fontSize: tokens.typography.sizes.sm,
          lineHeight: tokens.typography.lineHeights.normal,
          fontWeight: isActive ? tokens.typography.weights.bold : tokens.typography.weights.regular,
          color: isActive || isHovered ? accentColor : tokens.text.primary,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        };
        return (
          <button
            key={speed}
            type="button"
            role="option"
            aria-selected={isActive}
            style={itemStyle}
            onClick={() => onSelect(speed)}
            onMouseEnter={() => setHoveredSpeed(speed)}
            onMouseLeave={() =>
              setHoveredSpeed((current) => (current === speed ? null : current))
            }
          >
            {formatSpeedLabel(speed)}
          </button>
        );
      })}
    </div>
  );
}

const FADE_IN_DURATION_MS = 220;

export function AudioControls(): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const {
    isPlaying,
    togglePlaying,
    speed,
    setSpeed,
    openPanel,
    setOpenPanel,
  } = useAudioControls();

  const [hoveredControl, setHoveredControl] = useState<'play-pause' | 'speed' | null>(null);
  const [mounted, setMounted] = useState(false);
  const speedPopoverOpen = openPanel === 'speed';
  const wrapperRef = useRef<HTMLDivElement>(null);
  const iconColor = tokens.text.primary;
  const clusterVerticalPadding = isSmallMobile ? tokens.spacing.xs : tokens.spacing.md;

  // Defer the opacity flip until after the initial paint so the cluster
  // visibly transitions from 0 → 1 each time it mounts (audio turned on,
  // or current-block hand-off triggers a new render).
  useEffect(() => {
    const handle = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(handle);
  }, []);

  useEffect(() => {
    if (!speedPopoverOpen) return;
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (wrapperRef.current && wrapperRef.current.contains(target)) return;
      setOpenPanel(null);
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpenPanel(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [speedPopoverOpen, setOpenPanel]);

  // Placement comes from CourseSectionShell's sticky section-overlay slot, so
  // the cluster only has to lay itself out and centre within it.
  const containerStyle: CSSProperties = useMemo(
    () => ({
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      pointerEvents: 'none',
      opacity: mounted ? 1 : 0,
      transition: `opacity ${FADE_IN_DURATION_MS}ms ease-out`,
    }),
    [mounted],
  );

  // The cluster pins over authored content, so it carries its own opaque
  // backdrop. NA-357 adds the reviewed capsule spacing, with a smaller vertical
  // tier that keeps the controls inside the 50px narrow-phone section padding.
  const clusterStyle: CSSProperties = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      gap: `${CLUSTER_GAP_PX}px`,
      padding: `${clusterVerticalPadding} ${tokens.spacing.xl}`,
      borderRadius: tokens.radius.full,
      backgroundColor: tokens.backgrounds.page,
      boxShadow: POPOVER_SHADOW,
      fontFamily: tokens.typography.fontFamily,
      pointerEvents: 'auto',
    }),
    [
      clusterVerticalPadding,
      tokens.backgrounds.page,
      tokens.radius.full,
      tokens.spacing.xl,
      tokens.typography.fontFamily,
    ],
  );

  const playPauseStyle: CSSProperties = {
    boxSizing: 'border-box',
    width: `${PLAY_PAUSE_SIZE_PX}px`,
    height: `${PLAY_PAUSE_SIZE_PX}px`,
    borderRadius: tokens.radius.full,
    border: `1px solid ${tokens.borders.default}`,
    backgroundColor: hoveredControl === 'play-pause' ? HOVER_FILL : 'transparent',
    color: iconColor,
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 150ms ease',
  };

  const speedPillStyle: CSSProperties = {
    boxSizing: 'border-box',
    width: `${SPEED_PILL_WIDTH_PX}px`,
    height: `${SPEED_PILL_HEIGHT_PX}px`,
    // NA-357 restores the original rounded-bar shape so this control remains
    // visually distinct from the full-pill capsule and play/pause button.
    borderRadius: tokens.radius.xs,
    border: `1px solid ${tokens.borders.default}`,
    backgroundColor: hoveredControl === 'speed' ? HOVER_FILL : 'transparent',
    color: iconColor,
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: tokens.typography.sizes.sm,
    fontWeight: tokens.typography.weights.regular,
    lineHeight: tokens.typography.lineHeights.normal,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 150ms ease',
  };

  const speedWrapperStyle: CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const handleSpeedPillClick = (): void => {
    setOpenPanel(speedPopoverOpen ? null : 'speed');
  };

  const handleSpeedSelect = (next: AudioSpeed): void => {
    setSpeed(next);
    setOpenPanel(null);
  };

  return (
    <div
      style={containerStyle}
      data-testid="audio-controls"
      data-narration-control="true"
    >
      <div ref={wrapperRef} style={clusterStyle}>
        <Waveform isPlaying={isPlaying} color={iconColor} />
        <button
          type="button"
          style={playPauseStyle}
          aria-label={isPlaying ? t('audio_controls.pause') : t('audio_controls.play')}
          aria-pressed={!isPlaying}
          onClick={togglePlaying}
          onMouseEnter={() => setHoveredControl('play-pause')}
          onMouseLeave={() =>
            setHoveredControl((current) => (current === 'play-pause' ? null : current))
          }
        >
          <PlayPauseIcon isPlaying={isPlaying} color={iconColor} />
        </button>
        <div style={speedWrapperStyle}>
          <button
            type="button"
            style={speedPillStyle}
            aria-label={t('audio_controls.narration_speed_value', {
              speed: formatSpeedLabel(speed),
            })}
            aria-haspopup="listbox"
            aria-expanded={speedPopoverOpen}
            onClick={handleSpeedPillClick}
            onMouseEnter={() => setHoveredControl('speed')}
            onMouseLeave={() =>
              setHoveredControl((current) => (current === 'speed' ? null : current))
            }
          >
            {formatSpeedLabel(speed)}
          </button>
          {speedPopoverOpen && (
            <SpeedPopover
              selectedSpeed={speed}
              onSelect={handleSpeedSelect}
              capsuleVerticalPadding={clusterVerticalPadding}
            />
          )}
        </div>
      </div>
    </div>
  );
}

AudioControls.displayName = 'AudioControls';
